import { createAsyncThunk } from '@reduxjs/toolkit';

import config from '@/config';
import { GLSAxios } from '@/features/game/gls-axios';
import { mapFgpResponseToGameData } from '@/features/game/store/thunks/helpers/map-fgp-response';
import {
  FetchFgpGameDataInput,
  FetchFgpGameDataPayload,
  FetchFgpGameResponse,
  ProviderNameToFgp,
} from '@/features/game/types';
import { tokenDecoder } from '@/features/geolocation/token-decoder';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { FgpGameData } from '@/features/providers/types';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const fetchFgpGameData = createAsyncThunk<
  FgpGameData | undefined,
  FetchFgpGameDataInput
>('gameData/fetchFgpGameData', async (input, thunkApi) => {
  try {
    WrapperProxy.setFetchGameStarted();
    Logger.rumAction({
      name: 'Begin fetch game',
      useCase: DdEventCategory.FetchFgp,
      eventType: DdEventType.Info,
    });
    const axios = GLSAxios(input.fdState);
    const gameLaunchUrl = config.fgp.GLSInitPath;
    const tokenDetails = tokenDecoder(input.geolocationToken);

    const requestPayload: FetchFgpGameDataPayload = {
      authToken: input.authToken,
      loginToken: input.loginToken,
      sessionCode: input.sessionCode,
      platform: input.platform,
      userId: input.userId,
      provider: ProviderNameToFgp[input.provider],
      providerRef: input.providerRef,
      ip: input.ip,
      gameUid: input.gameUid,
      lobbyUrl: input.lobbyUrl,
      geolocationToken: input.geolocationToken,
      jackpotMode: input?.jackpotMode === 'true',
      product: input.product,
      channel: input.channel,
      ...(input?.slices && { slices: input.slices }),
    };

    const gameDataResponse = await axios.post<
      FetchFgpGameResponse<FgpGameData>
    >(gameLaunchUrl, requestPayload);

    if (gameDataResponse.data.error) {
      Logger.rumError({
        name: 'Failed to fetch game',
        useCase: DdEventCategory.FetchFgp,
        eventType: DdEventType.Error,
        context: {
          data: gameDataResponse.data.data,
          tokenDetails,
        },
      });
      WrapperProxy.setFetchGameFailure({
        errorCode: gameDataResponse.data.error.errorCode,
      });
      return thunkApi.rejectWithValue('FETCH_GAME_ERROR');
    }

    const gamingSessionTrackingId =
      gameDataResponse.data.data?.gamingSessionTrackingId;

    WrapperProxy.setFetchGameSuccess(
      gamingSessionTrackingId ? { gamingSessionTrackingId } : undefined,
    );
    Logger.rumAction({
      name: 'Game fetch success',
      useCase: DdEventCategory.FetchFgp,
      eventType: DdEventType.Success,
      context: {
        tokenDetails,
      },
    });

    return mapFgpResponseToGameData(gameDataResponse.data, input.provider);
  } catch (err) {
    WrapperProxy.setFetchGameFailure();
    Logger.rumError({
      name: 'General error',
      useCase: DdEventCategory.FetchFgp,
      eventType: DdEventType.Error,
      context: {
        error: err,
      },
    });
    return thunkApi.rejectWithValue('FGP_API_ERROR');
  }
});
